import React, { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { Form, Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import AccountRecoveryService from "../../services/account-recovery/accountRecovery";
import CameraCapture from "../Common/CameraCapture";
import "./AccountRecovery.css";

const RTL_LANGS = ["ar"];

const STEPS = [1, 2, 3, 4, 5, 6, 7];

const AccountRecovery = () => {
  const { t, i18n } = useTranslation();

  const toggleLanguage = useCallback(() => {
    const newLang = i18n.language === "ar" ? "en" : "ar";
    i18n.changeLanguage(newLang);
    localStorage.setItem("userLanguage", JSON.stringify({ code: newLang }));
    const dir = RTL_LANGS.includes(newLang) ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = newLang;
  }, [i18n]);

  useEffect(() => {
    const dir = RTL_LANGS.includes(i18n.language) ? "rtl" : "ltr";
    document.documentElement.dir = dir;
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);
  const { register, formState: { errors }, getValues } = useForm();

  const [activeStep, setActiveStep] = useState(1);
  const [requestToken, setRequestToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [uploadedDocs, setUploadedDocs] = useState(null);
  const [uploadedSelfie, setUploadedSelfie] = useState(null);
  const [docBlob, setDocBlob] = useState(null);
  const [selfieBlob, setSelfieBlob] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const startOtpTimer = useCallback(() => {
    setOtpTimer(30);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  const handleVerifyIr = async () => {
    const username = getValues("username");
    if (!username) return;
    setLoading(true);
    try {
      const res = await AccountRecoveryService.verifyIr({ username });
      if (res?.status) {
        setRequestToken(res.data?.requestToken);
        toast.success(t("ir_verified"));
        setActiveStep(2);
      } else {
        toast.error(res?.message || res?.description || "Verification failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const handleIdentitySubmit = async () => {
    const { fullName, phone, address } = getValues();
    if (!fullName || !phone || !address) return;
    setLoading(true);
    try {
      const res = await AccountRecoveryService.saveIdentity({
        requestToken,
        submittedName: fullName,
        submittedPhone: phone,
        submittedAddress: address,
      });
      if (res?.status) {
        const types = await AccountRecoveryService.getDocumentTypes();
        if (types?.data?.documentTypes) setDocumentTypes(types.data.documentTypes);
        setActiveStep(3);
      } else {
        toast.error(res?.message || "Failed to save identity");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to save identity");
    } finally {
      setLoading(false);
    }
  };

  const [docMeta, setDocMeta] = useState(null);
  const [selfieMeta, setSelfieMeta] = useState(null);

  const compressDocImage = useCallback((file, callback) => {
    const MAX_DIM = 1280;
    const MAX_BYTES = 5 * 1024 * 1024;

    if (!file.type.startsWith("image/") || file.type === "image/webp" || file.type === "application/pdf") {
      callback(file);
      return;
    }
    if (file.size <= MAX_BYTES) {
      callback(file);
      return;
    }

    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
      const w = Math.round(img.width * scale);
      const h = Math.round(img.height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, w, h);

      const tryBlob = (quality) => {
        canvas.toBlob(
          (blob) => {
            if (blob.size > MAX_BYTES && quality > 0.4) {
              tryBlob(quality - 0.1);
              return;
            }
            callback(blob);
          },
          "image/jpeg",
          quality
        );
      };

      if (file.size <= MAX_BYTES && scale === 1) {
        callback(file);
      } else {
        tryBlob(0.85);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      callback(file);
    };
    img.src = url;
  }, []);

  const handleDocFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    compressDocImage(file, (processed) => {
      setDocBlob(processed);
      setDocMeta({ captureSource: "file", capturedAt: new Date().toISOString() });
      setUploadedDocs(URL.createObjectURL(processed));
    });
  };

  const [docTypeError, setDocTypeError] = useState(false);

  const handleDocUpload = async () => {
    const documentType = getValues("documentType");
    if (!documentType) {
      setDocTypeError(true);
      toast.error(t("select_document_type_error", "Please select a document type"));
      return;
    }
    setDocTypeError(false);
    if (!docBlob) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestToken", requestToken);
      formData.append("docKind", "document");
      formData.append("captureSource", docMeta?.captureSource || "file");
      formData.append("capturedAt", docMeta?.capturedAt || new Date().toISOString());
      formData.append("kycCategoryId", documentType);
      formData.append("file", docBlob, "document.jpg");
      const res = await AccountRecoveryService.uploadDocuments(formData);
      if (res?.status) {
        setActiveStep(4);
      } else {
        toast.error(res?.message || "Upload failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSelfieCapture = (blob, metadata) => {
    setSelfieBlob(blob);
    setSelfieMeta(metadata);
    setUploadedSelfie(URL.createObjectURL(blob));
  };

  const handleSelfieUpload = async () => {
    if (!selfieBlob) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("requestToken", requestToken);
      formData.append("docKind", "selfie");
      formData.append("captureSource", selfieMeta?.captureSource || "camera");
      formData.append("capturedAt", selfieMeta?.capturedAt || new Date().toISOString());
      formData.append("file", selfieBlob, "selfie.jpg");
      const res = await AccountRecoveryService.uploadDocuments(formData);
      if (res?.status) {
        setActiveStep(5);
      } else {
        toast.error(res?.message || "Upload failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Upload failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async () => {
    const { newEmail, confirmEmail } = getValues();
    if (!newEmail || !confirmEmail) return;
    if (newEmail !== confirmEmail) {
      toast.error(t("emails_dont_match"));
      return;
    }
    setLoading(true);
    try {
      const res = await AccountRecoveryService.sendOtp({
        requestToken,
        newEmail,
      });
      if (res?.status) {
        toast.success(t("otp_sent"));
        setActiveStep(6);
        startOtpTimer();
      } else {
        const msg = res?.message === "email_unavailable" ? t("email_unavailable") : (res?.message || "Failed to send OTP");
        toast.error(msg);
      }
    } catch (err) {
      const msg = err?.response?.data?.message === "email_unavailable" ? t("email_unavailable") : (err?.response?.data?.message || "Failed to send OTP");
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setLoading(true);
    try {
      const res = await AccountRecoveryService.resendOtp({
        requestToken,
        newEmail: getValues("newEmail"),
      });
      if (res?.status) {
        toast.success(t("otp_sent"));
        startOtpTimer();
      } else {
        const msg = res?.message === "email_unavailable" ? t("email_unavailable") : (res?.message || "Failed to resend OTP");
        toast.error(msg);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to resend OTP");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    const otp = getValues("otp");
    if (!otp) return;
    setLoading(true);
    try {
      const res = await AccountRecoveryService.verifyOtp({
        requestToken,
        otp,
      });
      if (res?.status) {
        toast.success(t("otp_verified"));
        setActiveStep(7);
      } else {
        toast.error(res?.message || t("invalid_otp"));
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || t("invalid_otp"));
    } finally {
      setLoading(false);
    }
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    try {
      const res = await AccountRecoveryService.submitRequest({
        requestToken,
      });
      if (res?.status) {
        setSubmitted(true);
      } else {
        toast.error(res?.message || "Submission failed");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Submission failed");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="account-recovery-page">
        <div className="account-recovery-card">
          <div className="submitted-success">
            <h3>{t("request_submitted")}</h3>
            <p className="mt-3">{t("request_submitted_message")}</p>
          </div>
        </div>
      </div>
    );
  }

  const renderStep = () => {
    switch (activeStep) {
      case 1:
        return (
          <div>
            <h5 className="mb-3">{t("enter_ir_username")}</h5>
            <Form.Group className="mb-3">
              <Form.Label>{t("ir_username")}</Form.Label>
              <Form.Control
                type="text"
                {...register("username", { required: true })}
                placeholder={t("ir_username")}
              />
              {errors.username && (
                <Form.Text className="text-danger">
                  {t("this_field_is_required", "This field is required")}
                </Form.Text>
              )}
            </Form.Group>
            <div className="step-actions">
              <div />
              <Button
                variant="primary"
                onClick={handleVerifyIr}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" animation="border" /> : t("verify_ir")}
              </Button>
            </div>
          </div>
        );

      case 2:
        return (
          <div>
            <h5 className="mb-3">{t("identity_information")}</h5>
            <Form.Group className="mb-3">
              <Form.Label>{t("full_name")}</Form.Label>
              <Form.Control
                type="text"
                {...register("fullName", { required: true })}
                placeholder={t("full_name")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("phone_number")}</Form.Label>
              <Form.Control
                type="text"
                {...register("phone", { required: true })}
                placeholder={t("phone_number")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("address")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                {...register("address", { required: true })}
                placeholder={t("address")}
              />
            </Form.Group>
            <div className="step-actions">
              <Button variant="outline-secondary" onClick={() => setActiveStep(1)}>
                {t("back")}
              </Button>
              <Button
                variant="primary"
                onClick={handleIdentitySubmit}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" animation="border" /> : t("next")}
              </Button>
            </div>
          </div>
        );

      case 3:
        return (
          <div>
            <h5 className="mb-3">{t("upload_document")}</h5>
            <Form.Group className="mb-3">
              <Form.Label>{t("document_type")}</Form.Label>
              <Form.Select
                {...register("documentType", { required: true })}
                isInvalid={docTypeError}
                onChange={(e) => {
                  register("documentType").onChange(e);
                  if (e.target.value) setDocTypeError(false);
                }}
              >
                <option value="">{t("select_document_type")}</option>
                {documentTypes.map((dt) => (
                  <option key={dt.id} value={dt.id}>
                    {dt.category}
                  </option>
                ))}
              </Form.Select>
              {docTypeError && (
                <Form.Text className="text-danger">
                  {t("select_document_type_error", "Please select a document type")}
                </Form.Text>
              )}
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("upload_document_file")}</Form.Label>
              <Form.Control
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={handleDocFileChange}
              />
              <Form.Text className="text-muted">
                {t("accepted_formats", "JPEG, PNG, WebP, PDF")}
              </Form.Text>
            </Form.Group>
            {uploadedDocs && (
              <div className="mt-3 text-center">
                <img
                  src={uploadedDocs}
                  alt="Document"
                  style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8 }}
                />
              </div>
            )}
            <div className="step-actions">
              <Button variant="outline-secondary" onClick={() => setActiveStep(2)}>
                {t("back")}
              </Button>
              <Button
                variant="primary"
                onClick={handleDocUpload}
                disabled={loading || !docBlob}
              >
                {loading ? <Spinner size="sm" animation="border" /> : t("next")}
              </Button>
            </div>
          </div>
        );

      case 4:
        return (
          <div>
            <h5 className="mb-3">{t("take_selfie")}</h5>
            <p className="text-muted mb-3">{t("selfie_instruction")}</p>
            <CameraCapture
              mode="selfie"
              allowFileUpload={false}
              onCapture={handleSelfieCapture}
            />
            {uploadedSelfie && (
              <div className="mt-3 text-center">
                <img
                  src={uploadedSelfie}
                  alt="Selfie"
                  style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 8 }}
                />
              </div>
            )}
            <div className="step-actions">
              <Button variant="outline-secondary" onClick={() => setActiveStep(3)}>
                {t("back")}
              </Button>
              <Button
                variant="primary"
                onClick={handleSelfieUpload}
                disabled={loading || !selfieBlob}
              >
                {loading ? <Spinner size="sm" animation="border" /> : t("next")}
              </Button>
            </div>
          </div>
        );

      case 5:
        return (
          <div>
            <h5 className="mb-3">{t("enter_new_email")}</h5>
            <Form.Group className="mb-3">
              <Form.Label>{t("new_email")}</Form.Label>
              <Form.Control
                type="email"
                {...register("newEmail", { required: true })}
                placeholder={t("new_email")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("confirm_new_email")}</Form.Label>
              <Form.Control
                type="email"
                {...register("confirmEmail", { required: true })}
                placeholder={t("confirm_new_email")}
              />
            </Form.Group>
            <div className="step-actions">
              <Button variant="outline-secondary" onClick={() => setActiveStep(4)}>
                {t("back")}
              </Button>
              <Button
                variant="primary"
                onClick={handleSendOtp}
                disabled={loading}
              >
                {loading ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  t("send_verification_code")
                )}
              </Button>
            </div>
          </div>
        );

      case 6:
        return (
          <div>
            <h5 className="mb-3">{t("verification_code")}</h5>
            <p className="text-muted mb-3">{t("enter_otp")}</p>
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                className="otp-input"
                maxLength={6}
                {...register("otp", { required: true })}
                placeholder="------"
              />
            </Form.Group>
            <div className="resend-timer">
              {otpTimer > 0 ? (
                <span>
                  {t("resend_code_in")} {otpTimer}s
                </span>
              ) : (
                <span className="resend-link" onClick={handleResendOtp}>
                  {t("resend_code")}
                </span>
              )}
            </div>
            <div className="step-actions">
              <Button variant="outline-secondary" onClick={() => setActiveStep(5)}>
                {t("back")}
              </Button>
              <Button
                variant="primary"
                onClick={handleVerifyOtp}
                disabled={loading}
              >
                {loading ? <Spinner size="sm" animation="border" /> : t("verify_code")}
              </Button>
            </div>
          </div>
        );

      case 7:
        return (
          <div>
            <h5 className="mb-3">{t("review_request")}</h5>
            <div className="review-section">
              <div className="review-label">{t("ir_username")}</div>
              <div className="review-value">{getValues("username")}</div>
            </div>
            <div className="review-section">
              <div className="review-label">{t("full_name")}</div>
              <div className="review-value">{getValues("fullName")}</div>
            </div>
            <div className="review-section">
              <div className="review-label">{t("phone_number")}</div>
              <div className="review-value">{getValues("phone")}</div>
            </div>
            <div className="review-section">
              <div className="review-label">{t("address")}</div>
              <div className="review-value">{getValues("address") || "—"}</div>
            </div>
            <div className="review-section">
              <div className="review-label">{t("new_email")}</div>
              <div className="review-value">{getValues("newEmail")}</div>
            </div>
            <div className="step-actions">
              <Button variant="outline-secondary" onClick={() => setActiveStep(6)}>
                {t("back")}
              </Button>
              <Button
                variant="primary"
                onClick={handleFinalSubmit}
                disabled={loading}
              >
                {loading ? (
                  <Spinner size="sm" animation="border" />
                ) : (
                  t("submit_request")
                )}
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="account-recovery-page">
      <div className="account-recovery-card" style={{ position: "relative" }}>
        <button
          type="button"
          onClick={toggleLanguage}
          className="lang-switcher-btn lang-switcher-btn--card"
        >
          {i18n.language === "ar" ? "EN" : "عربي"}
        </button>
        <h2>{t("account_recovery")}</h2>
        <p className="subtitle">{t("cant_access_email")}</p>

        <ul className="progressbar px-lg-5 px-0 mb-4">
          {STEPS.map((step, i) => (
            <li
              key={i}
              className={`progressbar-list ${i + 1 <= activeStep ? "active" : ""}`}
            >
              {t("step")} {i + 1}
            </li>
          ))}
        </ul>

        {renderStep()}
      </div>
    </div>
  );
};

export default AccountRecovery;
